import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as AddIcon,
  Timeline as TimelineIcon,
  NavigateNext as NavigateNextIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import projectsService from '../services/projectsService';
import { Project, Revision, CreateRevisionForm, CreateRevisionResponse, RevisionName } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';

const ProjectRevisionsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // State management
  const [project, setProject] = useState<Project | null>(null);
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [revisionNames, setRevisionNames] = useState<RevisionName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  // Dialog states
  const [createRevisionOpen, setCreateRevisionOpen] = useState(false);
  const [confirmWarningsOpen, setConfirmWarningsOpen] = useState(false);
  const [pendingRevisionData, setPendingRevisionData] = useState<CreateRevisionForm | null>(null);

  // Form states
  const [revisionForm, setRevisionForm] = useState<CreateRevisionForm>({
    revision_name_id: 0,
  });

  // Load initial data
  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectData, revisionsData, revisionNamesData] = await Promise.all([
        loadProject(),
        projectsService.getRevisions(parseInt(projectId!)),
        projectsService.getRevisionNames(),
      ]);

      setProject(projectData);
      setRevisions(revisionsData.sort((a, b) => a.revision_name_id - b.revision_name_id));
      setRevisionNames(revisionNamesData);
      setError(null);
    } catch (err) {
      setError('Failed to load project revisions');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProject = async (): Promise<Project | null> => {
    try {
      const projects = await projectsService.getProjects();
      return projects.find(p => p.id === parseInt(projectId!)) || null;
    } catch (err) {
      console.error('Error loading project:', err);
      return null;
    }
  };

  const handleCreateRevision = async () => {
    try {
      // First validate the revision to check for warnings
      const validationResponse = await projectsService.validateProjectRevision(parseInt(projectId!), revisionForm);

      setCreateRevisionOpen(false);
      setRevisionForm({ revision_name_id: 0 });

      if (validationResponse.warnings && validationResponse.warnings.length > 0) {
        setWarnings(validationResponse.warnings);
        setPendingRevisionData(revisionForm);
        setConfirmWarningsOpen(true);
      } else {
        // No warnings, create the revision directly
        await projectsService.createRevision(parseInt(projectId!), revisionForm);
        loadData(); // Refresh the list
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate revision');
    }
  };

  const handleConfirmCreateRevision = async () => {
    try {
      if (pendingRevisionData) {
        const response: CreateRevisionResponse = await projectsService.createRevision(parseInt(projectId!), pendingRevisionData);
        setConfirmWarningsOpen(false);
        setPendingRevisionData(null);
        setWarnings([]);
        loadData(); // Refresh the list
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create revision');
    }
  };

  const handleCancelCreateRevision = () => {
    setConfirmWarningsOpen(false);
    setPendingRevisionData(null);
    setWarnings([]);
  };

  const handleRevisionClick = (revision: Revision) => {
    navigate(`/sandboxes/${projectId}/${revision.id}`);
  };

  const canCreateRevision = hasCapability(user, CAP.CREATE_PROJECT_REVISION);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <MuiLink component={Link} to="/projects" underline="hover" color="inherit">
          Projects
        </MuiLink>
        <MuiLink component={Link} to={`/projects/${projectId}/documents`} underline="hover" color="inherit">
          {project?.name || `Project ${projectId}`}
        </MuiLink>
        <Typography color="text.primary">Revisions</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Project Revisions
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {project?.name} - Manage project revisions and access sandboxes
          </Typography>
        </Box>

        {canCreateRevision && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateRevisionOpen(true);
              setWarnings([]); // Clear any previous warnings
            }}
            aria-label="Create new revision"
          >
            New Revision
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}


      <Grid container spacing={3}>
        {revisions.map((revision) => (
          <Grid item xs={12} md={6} lg={4} key={revision.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
              onClick={() => handleRevisionClick(revision)}
              role="button"
              tabIndex={0}
              aria-label={`View sandboxes for ${revision.revision_name || 'Unknown'} (Step ${revision.revision_step})`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRevisionClick(revision);
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 500 }}>
                    {revision.revision_name || 'Unknown'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Step ${revision.revision_step}`}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Revision ID: {revision.id}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Project: {revision.project_name || `ID ${revision.project_id}`}
                </Typography>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRevisionClick(revision);
                  }}
                  aria-label={`View sandboxes for ${revision.revision_name || 'Unknown'}`}
                >
                  View Sandboxes
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {revisions.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <TimelineIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No revisions found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first revision to start managing sandboxes and load cases.
          </Typography>
          {canCreateRevision && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateRevisionOpen(true);
                setWarnings([]); // Clear any previous warnings
              }}
            >
              Create Your First Revision
            </Button>
          )}
        </Box>
      )}

      {/* Create Revision Dialog */}
      <Dialog
        open={createRevisionOpen}
        onClose={() => setCreateRevisionOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-revision-title"
      >
        <DialogTitle id="create-revision-title">Create New Revision</DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateRevision(); }}>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel>Revision Type</InputLabel>
              <Select
                value={revisionForm.revision_name_id.toString()}
                onChange={(e) => setRevisionForm({ ...revisionForm, revision_name_id: parseInt(e.target.value) })}
                aria-describedby="revision-type-helper"
                MenuProps={getZoomCompensatedSelectMenuProps()}
              >
                {revisionNames.map((rn) => (
                  <MenuItem key={rn.id} value={rn.id.toString()}>
                    {rn.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography id="revision-type-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Select the revision type
            </Typography>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateRevisionOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!revisionForm.revision_name_id}
            >
              Create Revision
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Confirm Warnings Dialog */}
      <Dialog
        open={confirmWarningsOpen}
        onClose={handleCancelCreateRevision}
        maxWidth="sm"
        fullWidth
        aria-labelledby="confirm-warnings-title"
      >
        <DialogTitle id="confirm-warnings-title">Confirm Revision Creation</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              The following workflow violations were detected:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
          <Typography>
            Are you sure you want to proceed with creating this revision despite these warnings?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreateRevision} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCreateRevision}
            variant="contained"
            color="warning"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectRevisionsPage;